import { useState, useEffect, useRef } from "react";
import { Table, Skeleton, Row, Col, Input, Button, Form, Card } from "antd";

import { openNotification, paginationEnum } from "@seims/shared";
import { Messages, Notify } from "../../../services/custom/Messages";
import util from "../../../../src/utils/functions/custom";
import api from "../../../services/citizenscienceService";

import { recordError } from "../../../utils/logService";
import { SearchOutlined } from "@ant-design/icons";
import { OrganizationForm } from "./OrganizationForm";
import { SelectedOrganizations } from "./SelectedOrganizations";
import ContributeModal from "./ContributeModal";

const OrganizationList = ({
  formRef,
  onSelectedOrganization,
  selectedRowKeys,
  selectedGlobalOrganization,
  setSelectedRowKeys,
  setSelectedGlobalOrganization,
  preselectedOrgs,
  hasUpdated,
}) => {
  const [partners, setPartners] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isNotSearchable, setIsNotSearchable] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [otherContributions, setOtherContributions] = useState("");
  const [loading, setLoading] = useState(false);
  const [showContribModal, setShowContribModal] = useState(false);
  // This is for saving purposes
  const [previewOrgsContrib, setPreviewOrgsContrib] = useState({});
  const [orgsContributions, setOrgsContributions] = useState([]);
  const [displaySelectedOrganisations, setDisplaySelectedOrganisations] =
    useState(false);
  // This is for display purposes
  const [selectedRows, setSelectedRows] = useState([]);

  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [pageSize, setPageSize] = useState(paginationEnum.DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(paginationEnum.DEFAULT_MAX_TOTAL);
  const [currentPage, setCurrentPage] = useState(
    paginationEnum.DEFAULT_FIRST_PAGE
  );
  const [firstRecord, setFirstRecord] = useState(
    paginationEnum.DEFAULT_FIRST_RECORD
  );
  const [pagingDisabled, setPagingDisabled] = useState(false);

  let msg = "";
  const [form] = Form.useForm();
  const searchButtonRef = useRef(null);

  const formName = "OrganizationListForm";
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "1",
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: "descend",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "2",
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
    { title: "Location", dataIndex: "address", key: "3" },
  ];

  useEffect(() => {
    getPartners({ first: 0, pageSize: 10 });
  }, [hasUpdated]);

  useEffect(() => {
    autoSelect();
  }, []);

  const autoSelect = () => {
    if (preselectedOrgs && preselectedOrgs?.address) {
      const { cityOrTown = "", province = "" } = preselectedOrgs.address || {};
      const selectedRow = {
        id: preselectedOrgs.id,
        key: preselectedOrgs.id?.toString(),
        name: preselectedOrgs.name,
        type: preselectedOrgs.type,
        typeid: preselectedOrgs.typeid,
        address: `${province} (${cityOrTown})`,
        telephone: undefined,
        email: undefined,
        contributions: preselectedOrgs.contributions,
        otherContribution: preselectedOrgs.otherContribution,
      };
      const orgsList = [...selectedOrganizations, selectedRow];

      const newRowsKeys = [...selectedRowKeys, preselectedOrgs?.id.toString()];

      setSelectedRowKeys(newRowsKeys);
      setSelectedRows(orgsList);
      onSelectedOrganization(selectedRow);
    } else if (selectedGlobalOrganization.length > 0) {
      const newRowsKeys = selectedGlobalOrganization.map((c) =>
        c.id.toString()
      );

      setSelectedRowKeys(newRowsKeys);
      setSelectedRows(selectedGlobalOrganization);
      // onSelectedOrganization(selectedGlobalOrganization);
    }
  };

  const getPartners = async ({ first = 0, max = 10, searchText = "" }) => {
    const entity = "partners";

    try {
      setLoading(true);

      if (max > total) setTotal(max);

      const response = await api.getOrganizations({
        first: first,
        max: max,
        searchText: searchText,
      });

      if (response) {
        var partnerData = [];
        const { results: data, total } = response?.data;

        if (response.status === 204) {
          // msg = util.parseMessage(Messages.NO_DATA, "Partners");
          // openNotification("Partners", msg, Notify.ERROR);
          setPartners([]);
          setFilteredData([]);
          setLoading(false);
          setPagingDisabled(true);

          return data;
        }

        partnerData = data.map((element) => ({
          id: element.id,
          key: element.id.toString(),
          name: element.name,
          type: element.partnerType.value,
          address: `${element.address?.province} (${element.address?.cityOrTown})`,
          typeid: element.partnerType.key,
        }));

        setPartners(partnerData);
        setFilteredData(partnerData);
        setLoading(false);
        setTotal(total);
        setPagingDisabled(false);
        return data;
      }
    } catch (error) {
      msg = util.parseMessage(Messages.GET_FAILURE, entity);
      openNotification("Partners", msg, Notify.ERROR);
      recordError(error);
      return [];
      // setLoading(false);
    }
  };

  const onUpdateContributions = (selected) => {
    let previewContribution = {};
    if (selected) {
      if (selectedGlobalOrganization.length === 0) {
        //LocalOrganization
        previewContribution = orgsContributions.find(
          (c) => c.id === selected.id
        );
      } else {
        //GlobalOrganization
        previewContribution = selectedGlobalOrganization.find(
          (c) => c.id === selected.id
        );
      }

      if (previewContribution) {
        setPreviewOrgsContrib(previewContribution);
      } else {
        setPreviewOrgsContrib(selected);
      }
      onShowContributeModal(true);
    }
  };

  const onRemoveContributions = (selectedId) => {
    let previewContribution = {};

    if (selectedId > 0) {
      if (selectedGlobalOrganization.length === 0) {
        previewContribution = orgsContributions.find(
          (c) => c.id === selectedId
        );
      } else {
        previewContribution = selectedGlobalOrganization.find(
          (c) => c.id === selectedId
        );
      }

      if (previewContribution) {
        setPreviewOrgsContrib([]);
      }

      const itemsToKeep = selectedGlobalOrganization.filter(
        (c) => c.id !== selectedId
      );

      const selectedRows = itemsToKeep.map((c) => c.id.toString());

      setSelectedRowKeys(selectedRows);
      setSelectedRows(itemsToKeep);
      setSelectedGlobalOrganization(itemsToKeep);
      // onSelectedOrganization(itemsToKeep);
    }
  };

  const onPageChange = (page, total) => {
    const keepCurrentPage = pageSize === total;
    const first = keepCurrentPage ? (page - 1) * pageSize : 0;
    const newPage = keepCurrentPage ? page : 1;

    // onSelectedOrganization(selectedGlobalOrganization);

    setFirstRecord(first);
    setPageSize(total);
    setCurrentPage(newPage);
    getPartners({ first: first, max: pageSize, searchText: searchText });
    autoSelect();
  };

  const onRowChange = (row) => {
    setPageSize(row.pageSize);
  };

  const handleInputChange = (e) => {
    const { value: searchValue } = e.target;
    setSearchText(searchValue);
    setIsNotSearchable(searchValue === "");

    if (searchValue === "") {
      setFirstRecord(0);
      setCurrentPage(1);
      getPartners({ first: 0, pageSize: 10, searchText: searchValue });
    } else {
      // globalSearch(searchValue);
      getPartners({ first: 0, pageSize: 10, searchText: searchValue });
    }
  };

  const handleSearchReset = (e) => {
    const { value: searchValue } = e.target;
    setSearchText(searchValue);
    setIsNotSearchable(searchValue === "");

    if (searchValue === "") {
      getPartners({ first: 0, pageSize: 10, searchText: searchValue });
    }
  };

  const handleSearchBtn = async () => {
    setFirstRecord(0);
    return await getPartners({
      first: 0,
      pageSize: 10,
      searchText: searchText,
    });
  };

  const onSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
  };

  const handleSelectedContributions = (selectedContributions) => {
    // setOrgsContributions([...orgsContributions, selectedContributions]);
    onSelectedOrganization(selectedContributions);
  };

  const onShowContributeModal = (value) => {
    setShowContribModal(value);

    //? You need to check if the number of selected organizations matches with the checked grid items
    //? If there are more checked items then remove the ones not in the selected list
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) =>
      onSelectChange(selectedRowKeys, selectedRows),
    onSelect: (record, selected, selectedRows) => {
      if (selected) {
        //! PLEASE REVISIT selectedOrganization, must be removed since it's a local state manager and there's a global one
        setSelectedOrganizations([record, ...selectedOrganizations]);
        onUpdateContributions(record);
        onShowContributeModal(true);
        setDisplaySelectedOrganisations(true);
      } else {
        console.log("ROW Selection")
        onShowContributeModal(false);
        setDisplaySelectedOrganisations(false);

        let updSelection = [];
        if (selectedRows.length > 0) {
          updSelection = selectedOrganizations.filter(
            (c) => c.id !== record.id
          );
        } else {
          updSelection = [];
          setDisplaySelectedOrganisations(false);
        }

        //onUpdateContributions(record);
        // setSelectedOrganizations([...selectedOrganizations, updSelection]);
        // setSelectedOrganizations(updSelection);
        onRemoveContributions(record.id);
      }
    },
  };

  const atLeastOneSelected = (selectedRows) => ({
    validator(_, value) {
      if (selectedRows.length > 0) {
        return Promise.resolve();
      }
      // return Promise.reject(new Error('Please select at least one organization.'));
      return openNotification(
        "Organization",
        "Please select at least one organization",
        Notify.ERROR
      );
    },
  });

  return (
    <>
      <div>
        {selectedRowKeys.length > 0 && (
          <Card>
            <div style={{ textAlign: "left" }}>
              <SelectedOrganizations
                key={"orgs"}
                selectedOrgs={selectedGlobalOrganization}
                onShowContributeModal={onUpdateContributions}
                onRemoveContr={onRemoveContributions}
              />
            </div>
          </Card>
        )}
        <br></br>
        <Card>
          <Row style={{ paddingTop: "25px" }}>
            <Col xs={24} md={6}>
              <Input
                placeholder="Search for a Partner"
                type="text"
                className="search-bar"
                onChange={handleSearchReset}
                // onBlur={handleInputChange}
                onPaste={handleInputChange}
                onPressEnter={handleInputChange}
                value={searchText}
                allowClear
                size="large"
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={24} md={2}>
              <Button
                disabled={isNotSearchable}
                key="Search"
                value="Search"
                type="primary"
                shape="round"
                onClick={handleSearchBtn}
                style={{ marginLeft: 10, marginTop: 5 }}
                className="yel-button"
                ref={searchButtonRef}
              >
                Search
              </Button>
            </Col>
            {/* <p style={{paddingTop:"6px", fontSize:"14px", fontWeight:"bold", paddingLeft:"90px"}}>Or</p> */}
          </Row>
          <div className="space"></div>
          {loading ? (
            <Skeleton active avatar />
          ) : (
            <div>
              <Form form={form} name={formName} ref={formRef}>
                <Form.Item
                  name="organization"
                  rules={[atLeastOneSelected(selectedRowKeys)]}
                  className="custom-error-message"
                >
                  <Table
                    dataSource={filteredData}
                    rowKey={(record) => record.key}
                    columns={columns}
                    loading={loading}
                    // onChange={onRowChange}
                    bordered
                    rowSelection={rowSelection}
                    pagination={{
                      disabled: pagingDisabled,
                      defaultCurrent: 1,
                      current: currentPage,
                      defaultPageSize: pageSize,
                      total: total,
                      pageSize: pageSize,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      onChange: onPageChange,
                      pageSizeOptions: [10, 20, 50, 100],
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} ${
                          total > 1 ? "Organizations" : "Organization"
                        }`,
                    }}
                    size="middle"
                  />
                </Form.Item>
              </Form>
            </div>
          )}
        </Card>
        <ContributeModal
          key={"contrModal"}
          onShow={onShowContributeModal}
          show={showContribModal}
          previewOrgsContrib={previewOrgsContrib}
          setOrgsContributions={handleSelectedContributions}
        />
      </div>
    </>
  );
};

export default OrganizationList;
